import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { jwtDecode, type JwtPayload } from "jwt-decode";
import type { Moment } from "moment";
import moment from "moment";
import { useEffect, useState } from "react";

type RefreshingAccessToken = {
    accessToken: string;
    accessTokenExpiry: Moment;
    refreshAccessToken: () => void;
};

export function useRefreshingAccessToken({ onTokenExpired }: { onTokenExpired?: () => void }): RefreshingAccessToken {
    const [hasTokenExpired, setHasTokenExpired] = useState(false);

    const { result, refetch: refreshAccessToken } = useInlineStatusQuery(
        async (repository) => {
            const newToken = await repository.Users.generateAccessToken();
            const payload = jwtDecode<JwtPayload>(newToken.AccessToken);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const expiryTime = moment(payload.exp! * 1000); //The payload time is seconds since Epoch, need to convert to milliseconds
            return { accessToken: newToken.AccessToken, accessTokenExpiry: expiryTime };
        },
        [],
        "useRefreshingAccessToken"
    );

    // initialResult has an expiry in the past
    const initialResult = { accessToken: "", accessTokenExpiry: moment().subtract(moment.duration(1).asHours()) };
    const { accessToken, accessTokenExpiry } = result ?? initialResult;

    useEffect(() => {
        const pollingIntervalMs = 5000;
        const accessTokenExpiryCheckIntervalId: ReturnType<typeof setTimeout> = setInterval(async () => {
            const tokenExpired = accessTokenExpiry != undefined && accessTokenExpiry < moment();
            if (tokenExpired !== hasTokenExpired) {
                onTokenExpired?.();
            }

            setHasTokenExpired(tokenExpired);
        }, pollingIntervalMs);

        return () => {
            clearInterval(accessTokenExpiryCheckIntervalId);
        };
    });

    return { accessToken, accessTokenExpiry, refreshAccessToken };
}
